import React, {useState} from 'react';
import b_ from 'b_';

import {
    AudienceCategories,
    AudienceInterestPayload,
    AudienceRequest,
    AudienceType,
    Quota
} from 'client/common/types';
import {useBoxType} from 'client/common/hooks/box';
import {useUserRoles} from 'client/common/hooks/user';
import {useAudienceCategories, useUpdateAudienceRequest} from 'client/common/hooks/audience';

import {AudienceSizeDO, AudienceTargeting} from 'client/common/utils/audience';
import {unsetError} from 'client/common/utils/errors';

import {Spin} from 'client/common/components/lego';
import Size from 'client/common/components/audience-settings/size';

const b = b_.with('audience-settings');

interface SizeWrapperProps {
    request: AudienceRequest;
    slug: string;
    disabled?: boolean;
}

export default function SizeWrapper(props: SizeWrapperProps) {
    const {slug, request, disabled} = props;

    const boxType = useBoxType(slug);
    const userRoles = useUserRoles();
    const categories = useAudienceCategories(slug);

    const onAudienceUpdate = useUpdateAudienceRequest(slug);

    const [size, setSize] = useState(request.size);

    if (categories.status !== 'success') {
        return <Spin className={b('spin')} size='xs' view='default' progress />;
    }

    const selectedCountry = AudienceTargeting.getSelectedCountry({
        request,
        boxType,
        categories: categories.data
    });

    const sizeError = new AudienceSizeDO({
        boxType,
        targetingCountry: selectedCountry,
        size,
        type: request.type,
        categories: request.categories,
        userRoles
    }).getSizeError();

    // eslint-disable-next-line max-statements
    const handleUpdate = async (sz: number, reset?: boolean) => {
        const nothingChanged = request.size === sz;

        if (isNaN(sz) || nothingChanged) return;

        const validSize = new AudienceSizeDO({
            boxType,
            targetingCountry: selectedCountry,
            size: sz,
            type: request.type,
            categories: request.categories,
            userRoles
        }).getValidSize();

        const update: {
            size: number;
            type: AudienceType;
            quotas: Quota[] | null;
            interests: AudienceInterestPayload[] | null;
            categories: AudienceCategories[];
            segments?: string[];
        } = {
            size: validSize,
            type: request.type,
            quotas: null,
            interests: null,
            categories: request.categories
        };

        if (request.type === AudienceType.YaAudience) {
            update.segments = request.segments;
        }

        if (request.type === AudienceType.MediaTargeting) {
            update.interests = request.interests;
        }

        if (request.type === AudienceType.Quotas) {
            update.quotas = (request.quotas || []).map(quota => ({
                type: quota.type,
                categories: quota.categories,
                interests: quota.interests,
                segments: quota.segments,
                sizePerc: quota.sizePerc
            }));
        }

        await onAudienceUpdate(update);

        unsetError('size');

        if (reset) {
            setSize(validSize);
        }
    };

    return (
        <Size
            size={size}
            errorMsg={sizeError}
            handleUpdate={handleUpdate}
            onChange={setSize}
            disabled={disabled}
        />
    );
}
